import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import Files from "react-files";
import { Button, Modal } from "../components/Modal";
import { MembershipType } from "../organizations/OrganizationStore";
import * as UserStore from "./UserStore";

interface ImportUsersModalProps {
    isOpen: boolean;
    organizationId: string;
    onClose: () => void;
    onImported: () => void;
}

const UpdatedField = ({ oldValue, newValue }) => oldValue !== newValue ? (
    <><del className="text-gray-500">{oldValue}</del><span className="text-yellow-600 ml-1">{newValue}</span></>
) : <span>{newValue}</span>;

export const ImportUsersModal = ({ isOpen, organizationId, onClose, onImported }: ImportUsersModalProps) => {
    const [fileName, setFileName] = React.useState("");
    const [fileData, setFileData] = React.useState("");
    const [preview, setPreview] = React.useState([] as any[]);
    const [page, setPage] = React.useState(0);
    const [keepExistingProfile, setKeepExistingProfile] = React.useState(true);
    const [error, setError] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;
        setFileName("");
        setFileData("");
        setPreview([]);
        setPage(0);
        setKeepExistingProfile(true);
        setError("");
        setIsSubmitting(false);
    }, [isOpen]);

    const onFilesChange = (files) => {
        if (!files.length || !files[0]) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const result = event.target?.result?.toString();
            if (!result) return;
            setFileData(result.substring(result.indexOf("base64,") + 7));
            setFileName(files[0].name);
            setPreview([]);
            setError("");
        };
        reader.readAsDataURL(files[0]);
    };

    const previewImport = () => {
        setIsSubmitting(true);
        setError("");
        UserStore.ImportUsersFromExcelTestRun(organizationId, fileData, keepExistingProfile)
            .then((response) => {
                setPreview(response.data);
                setPage(0);
            })
            .catch((request) => setError(request.response?.data || "Unable to preview this import."))
            .finally(() => setIsSubmitting(false));
    };

    const importUsers = () => {
        setIsSubmitting(true);
        setError("");
        UserStore.ImportUsersFromExcel(organizationId, fileData, keepExistingProfile)
            .then(() => {
                onImported();
                onClose();
            })
            .catch((request) => setError(request.response?.data || "Unable to import users."))
            .finally(() => setIsSubmitting(false));
    };

    const lastPage = Math.max(0, Math.ceil(preview.length / 25) - 1);

    return (
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={!isSubmitting} title="Import Users" icon={["fad", "upload"]} iconColor="text-green-600" size="xl">
            <div className="p-6 max-h-[75vh] overflow-y-auto">
                {preview.length === 0 ? (
                    <div className="space-y-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">Upload an Excel file (.xlsx) to import multiple users.</div>
                        <label className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md cursor-pointer">
                            <input type="checkbox" className="mt-1" checked={keepExistingProfile} onChange={(event) => setKeepExistingProfile(event.target.checked)} />
                            <div>
                                <div className="text-sm font-medium text-yellow-900">Keep existing user profile data</div>
                                <div className="text-xs text-yellow-800">Recommended for partial spreadsheets. Existing users will only be added to this organization membership.</div>
                            </div>
                        </label>
                        <Files className="files-dropzone" onChange={onFilesChange} onError={(fileError) => setError(fileError.message)} accepts={[".xlsx"]} maxFileSize={10000000} minFileSize={0} clickable multiple={false}>
                            <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-gray-400 transition-colors">
                                <FontAwesomeIcon icon={["fad", "cloud-upload"]} className="w-8 h-8 text-gray-400 mb-2" />
                                <div className="text-sm text-gray-600">{fileName || "Drop file here or click to upload"}</div>
                            </div>
                        </Files>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="bg-white shadow sm:rounded-md overflow-x-auto">
                            <table className="min-w-[980px] w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50"><tr>
                                    {["Type", "Name", "Email", "Phone", "Source", "Source ID", "Status"].map((heading) => <th key={heading} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{heading}</th>)}
                                </tr></thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {preview.slice(page * 25, (page + 1) * 25).map(({ user, isNew, isUpdate, existingUser, membershipOnlyForExisting }, index) => {
                                        const shownUser = membershipOnlyForExisting ? existingUser : user;
                                        return <tr key={shownUser?.id || index} className="hover:bg-gray-50">
                                            <td className="px-3 py-4 whitespace-nowrap text-sm">{MembershipType(user.type)}</td>
                                            {["fullName", "email", "phoneNumber", "source", "sourceID"].map((field) => <td key={field} className="px-3 py-4 whitespace-nowrap text-sm">
                                                {isNew ? <span className="text-green-600 font-medium">{user?.[field]}</span> : membershipOnlyForExisting ? <span>{existingUser?.[field]}</span> : <UpdatedField oldValue={existingUser?.[field]} newValue={user?.[field]} />}
                                            </td>)}
                                            <td className="px-3 py-4 whitespace-nowrap">
                                                {membershipOnlyForExisting && <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Membership only</span>}
                                                {isUpdate && <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Updated</span>}
                                                {isNew && <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">New</span>}
                                            </td>
                                        </tr>;
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-between items-center">
                            <Button variant="outline-secondary" onClick={() => setPage(Math.max(page - 1, 0))} disabled={page === 0}>Previous</Button>
                            <span className="text-sm text-gray-600">Page {page + 1} of {lastPage + 1}</span>
                            <Button variant="outline-secondary" onClick={() => setPage(Math.min(page + 1, lastPage))} disabled={page === lastPage}>Next</Button>
                        </div>
                    </div>
                )}
                {error && <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">{String(error)}</div>}
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-between items-center">
                <a href="api/integrations/downloadUserExcelImportTemplate" download="User Import Template.xlsx" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"><FontAwesomeIcon icon={["fad", "download"]} className="w-4 h-4 mr-1" />Download template</a>
                <div className="flex space-x-3">
                    <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    {fileData && preview.length === 0 && <Button variant="outline-primary" onClick={previewImport} disabled={isSubmitting}>Preview</Button>}
                    {preview.length > 0 && <Button onClick={importUsers} disabled={isSubmitting}>{isSubmitting ? "Importing…" : "Confirm import"}</Button>}
                </div>
            </div>
        </Modal>
    );
};
